/**
 * ScanStatus - Shows job status for Assets and Vulnerabilities
 *
 * Uses shared ScanStatus components for unified UI across Asset,
 * Vulnerability, and Capability drawers.
 *
 * Performance optimization: Uses useJobsBySource for vulnerability context
 * to avoid waterfall API pattern (1 call vs 2 sequential).
 */
import { useMemo, useState } from 'react';

import {
  getScanStatusConfig,
  ScanStatusBox,
  ScanStatusModal,
} from '@/components/ScanStatus';
import { useCancelJob, useJobs, useReRunJob } from '@/hooks/useJobs';
import { useJobsBySource } from '@/hooks/useJobsBySource';
import { Asset, Job, JobStatus, Risk } from '@/types';
import { mergeJobStatus } from '@/utils/api';

interface ScanStatusProps {
  asset?: Asset;
  risk?: Risk;
}

export function ScanStatus({ asset, risk }: ScanStatusProps) {
  const [open, setOpen] = useState(false);
  const { mutateAsync: reRunJob } = useReRunJob();
  const { mutateAsync: cancelJob } = useCancelJob();

  // Use dedicated hook for vulnerability context (single API call)
  const {
    data: riskJobs,
    status: riskJobsStatus,
    error: riskJobsError,
    refetch: refetchRiskJobs,
  } = useJobsBySource(risk?.source);

  // Use existing hook for asset context
  const {
    data: assetJobs,
    status: assetJobsStatus,
    error: assetJobsError,
    refetch: refetchAssetJobs,
  } = useJobs({
    query: asset ? `#job#${asset.group}#${asset.identifier}` : '',
    search: '',
    failedReason: '',
  });

  // Select appropriate data based on context
  const jobs: Job[] = risk ? riskJobs : assetJobs;
  const jobsStatus = risk ? riskJobsStatus : assetJobsStatus;
  const jobsError = risk ? riskJobsError : assetJobsError;
  const refetch = risk ? refetchRiskJobs : refetchAssetJobs;

  const scanStatus = useMemo(() => {
    if (!jobs.length) return undefined;
    return mergeJobStatus(jobs.map(job => job.status.slice(0, 2) as JobStatus));
  }, [jobs]);

  const statusConfig = useMemo(
    () => getScanStatusConfig(scanStatus),
    [scanStatus]
  );

  const actionHandlers = useMemo(
    () => ({
      handleRerun: (jobKey: string) => reRunJob({ jobKey }),
      handleCancel: (jobKey: string) => cancelJob({ jobKey }),
    }),
    [reRunJob, cancelJob]
  );

  const modalTitle = asset
    ? `${asset.identifier} Scans`
    : risk
      ? `${risk.name} Scans`
      : 'Scans';

  return (
    <>
      <ScanStatusBox
        statusConfig={statusConfig}
        isLoading={jobsStatus === 'pending'}
        onViewDetails={() => setOpen(true)}
        disabled={!scanStatus}
      />
      <ScanStatusModal
        open={open}
        onClose={() => setOpen(false)}
        title={modalTitle}
        jobs={jobs}
        isLoading={jobsStatus === 'pending'}
        error={jobsError}
        refetch={refetch}
        actionHandlers={actionHandlers}
      />
    </>
  );
}
